import { DEMO_API_URL, isDemoHost } from "./demo";

export interface Environment {
  id: string;
  name: string;
  apiUrl: string;
}

export const ENVIRONMENTS: Environment[] = [
  {
    id: "local",
    name: "Local Dev",
    apiUrl: "http://localhost:8080",
  },
  {
    id: "qa",
    name: "QA",
    apiUrl: "https://qa-api.greenbackapp.com",
  },
  {
    id: "production",
    name: "Production",
    apiUrl: "https://api.greenbackapp.com",
  },
];

// The Live sample (PRD 117). Deliberately NOT in ENVIRONMENTS: that list is
// what the ?api_env= override resolves against, and no link may point a real
// build at the demo api. Chosen only by the page's own hostname.
export const DEMO_ENVIRONMENT: Environment = {
  id: "demo",
  name: "Live sample",
  apiUrl: DEMO_API_URL,
};

// Additional QA environments, qa2 .. qa99, each with its own API at
// https://qaN-api.greenbackapp.com (created with api/bin/qa-env). They are not
// listed above because they come and go; any valid name resolves on demand.
// The same naming rule as the deploy workflows.
const QA_INSTANCE_ID = /^qa([2-9]|[1-9][0-9])$/;

export function qaInstanceEnvironment(id: string): Environment | undefined {
  if (!QA_INSTANCE_ID.test(id)) return undefined;
  return { id, name: `QA ${id.slice(2)}`, apiUrl: `https://${id}-api.greenbackapp.com` };
}

function findEnvironment(id: string): Environment | undefined {
  return ENVIRONMENTS.find(env => env.id === id) ?? qaInstanceEnvironment(id);
}

class EnvironmentConfigManager {
  private currentEnvironment: Environment | null = null;

  constructor() {
    this.loadSelectedEnvironment();
  }

  public getEnvironment(): Environment {
    return this.currentEnvironment || this.getDefaultEnvironment();
  }

  public getApiUrl(): string {
    const env = this.getEnvironment();
    return env.apiUrl;
  }

  public setEnvironment(environmentId: string): void {
    const environment = findEnvironment(environmentId);
    if (!environment) {
      throw new Error(`Environment with ID ${environmentId} not found`);
    }

    this.currentEnvironment = environment;
    localStorage.setItem("selectedEnvironment", environmentId);
  }

  private getDefaultEnvironment(): Environment {
    if (isDemoHost()) return DEMO_ENVIRONMENT;
    const isDev = process.env.NODE_ENV === "development";
    if (isDev) return ENVIRONMENTS[0]; // local

    // Build-time env var set in Amplify Console (e.g. REACT_APP_DEPLOYMENT_ENV=qa)
    const deploymentEnv = process.env.REACT_APP_DEPLOYMENT_ENV;
    if (deploymentEnv) {
      const matched = findEnvironment(deploymentEnv);
      if (matched) return matched;
    }

    return ENVIRONMENTS[ENVIRONMENTS.length - 1]; // production
  }

  private loadSelectedEnvironment(): void {
    // The demo host talks to the demo api and nothing else: checked before
    // the ?api_env= override so a crafted link cannot point the demo build at
    // production (where a real session could be read under the demo banner).
    if (isDemoHost()) {
      this.currentEnvironment = DEMO_ENVIRONMENT;
      return;
    }

    // Check for WebView environment override via query parameter.
    // The mobile app passes ?api_env=qa|prd|lcl so the web app
    // authenticates against the same API the native app uses.
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const apiEnvOverride = urlParams.get("api_env");
      if (apiEnvOverride) {
        const envMap: Record<string, string> = { lcl: "local", qa: "qa", prd: "production" };
        const mappedId = envMap[apiEnvOverride] || apiEnvOverride;
        const overrideEnv = findEnvironment(mappedId);
        if (overrideEnv) {
          this.currentEnvironment = overrideEnv;
          return;
        }
      }
    } catch {
      // URL parsing failed — fall through to default
    }

    // In non-development builds, use the deployment environment (QA or production)
    const isDev = process.env.NODE_ENV === "development";
    if (!isDev) {
      this.currentEnvironment = this.getDefaultEnvironment();
      return;
    }

    // On localhost development, default to local API to avoid accidental QA/production routing
    try {
      const hostname = window.location.hostname;
      const isLocalHost = hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";
      if (isLocalHost) {
        const localEnv = ENVIRONMENTS.find(env => env.id === "local");
        if (localEnv) {
          this.currentEnvironment = localEnv;
          return;
        }
      }
    } catch {
      // fall through to localStorage/default behavior
    }

    // Only in development: allow environment switching via localStorage
    try {
      const savedEnvironmentId = localStorage.getItem("selectedEnvironment");
      if (savedEnvironmentId) {
        const environment = findEnvironment(savedEnvironmentId);
        if (environment) {
          this.currentEnvironment = environment;
        }
      }
    } catch {
      this.currentEnvironment = this.getDefaultEnvironment();
    }
  }
}

export const EnvironmentConfig = new EnvironmentConfigManager();

/**
 * Feature flag keys for API-fetched flags
 * Use these with featureFlagClient.isEnabled() or isEnabledAsync()
 */
export const FEATURE_FLAG_KEYS = {
  DISABLE_REGISTRATION: "disable_registration",
} as const;

/**
 * Feature flags - compile-time fallback values
 * These are used when API is unavailable or during initial load
 */
export const FeatureFlags = {
  /**
   * Default fallback for disable_registration flag
   * When true, shows a link to beta signup instead of registration
   * The actual value is fetched from the API
   */
  DISABLE_REGISTRATION_DEFAULT: false,

  /**
   * Beta signup URL - shown when registration is disabled
   */
  BETA_SIGNUP_URL: "https://www.greenbackapp.com/join-beta",
} as const; 

/**
 * True for local dev and QA builds, never production. Gates developer
 * tooling (Settings → Developer) the same way the mobile app's
 * isDevOrAlphaBuild() gates its Developer Tools section.
 */
export const IS_DEV_OR_QA_BUILD =
  process.env.NODE_ENV === "development" ||
  process.env.REACT_APP_DEPLOYMENT_ENV === "qa" ||
  QA_INSTANCE_ID.test(process.env.REACT_APP_DEPLOYMENT_ENV ?? "");
